/**
 * Settings Store
 *
 * Provides global app settings with localStorage persistence.
 * Uses React Context for state management.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'

import { useOrganization } from './organization-store'

import type { PhoneValue } from '../components/ui'

// ============================================================================
// Types
// ============================================================================

export type DateFormat = 'MM/dd/yyyy' | 'dd/MM/yyyy' | 'yyyy-MM-dd'
export type NumberFormat = 'en-US' | 'en-IN' | 'de-DE'
export type DeliveryType = 'intrastate' | 'interstate' | 'export' | 'sez'
export type BusinessType = 'physical' | 'service' | 'digital'

export interface DocumentTypeDisplayConfig {
  show_logo: boolean
  show_signature: boolean
  show_bank_details: boolean
  show_terms: boolean
  show_declaration: boolean
  terms_text: string
  declaration_text: string
  show_gstin: boolean
  show_pan: boolean
  show_cin: boolean
  show_tan: boolean
  show_fssai: boolean
  show_msme: boolean
  show_ie_code: boolean
  show_lut_number: boolean
}

export type DocumentTypeKey =
  | 'b2b'
  | 'export'
  | 'b2c'
  | 'proforma'
  | 'credit_note'
  | 'debit_note'
  | 'vendor_bill'
  | 'vendor_credit_note'
  | 'purchase_order'
  | 'customer_purchase_order'

// SharedDocumentSettings holds Terms & Declaration applied to EVERY document
// type (managed on the "Terms & Declaration" tab). Replaces the per-doc-type
// show_terms/terms_text/declaration_text on DocumentTypeDisplayConfig, which
// are retained on the type for back-compat but no longer driven by the UI.
export interface SharedDocumentSettings {
  show_terms: boolean
  show_declaration: boolean
  terms_text: string
  declaration_text: string
}

// DocumentDisplayConfig = the per-document-type toggle map PLUS the shared
// Terms & Declaration block. The per-doc entries live under `byType`.
export type DocumentDisplayConfig = Record<DocumentTypeKey, DocumentTypeDisplayConfig> &
  SharedDocumentSettings

export const DOCUMENT_TYPE_LABELS: Record<DocumentTypeKey, string> = {
  b2b: 'B2B Invoice',
  export: 'Export Invoice',
  b2c: 'B2C / Bill of Supply',
  proforma: 'Proforma Invoice',
  credit_note: 'Credit Note',
  debit_note: 'Debit Note',
  vendor_bill: 'Vendor Bill',
  vendor_credit_note: 'Vendor Credit Note',
  purchase_order: 'Purchase Order',
  customer_purchase_order: 'Customer Purchase Order',
}

const DEFAULT_DOMESTIC_CFG: DocumentTypeDisplayConfig = {
  show_logo: true,
  show_signature: true,
  show_bank_details: true,
  show_terms: true,
  show_declaration: true,
  terms_text: '',
  declaration_text: '',
  show_gstin: true,
  show_pan: false,
  show_cin: false,
  show_tan: false,
  show_fssai: false,
  show_msme: false,
  show_ie_code: false,
  show_lut_number: false,
}

const DEFAULT_EXPORT_CFG: DocumentTypeDisplayConfig = {
  show_logo: true,
  show_signature: true,
  show_bank_details: true,
  show_terms: true,
  show_declaration: true,
  terms_text: '',
  declaration_text: '',
  show_gstin: false,
  show_pan: false,
  show_cin: false,
  show_tan: false,
  show_fssai: false,
  show_msme: false,
  show_ie_code: true,
  show_lut_number: true,
}

export const DEFAULT_SHARED_DOCUMENT_SETTINGS: SharedDocumentSettings = {
  show_terms: true,
  show_declaration: true,
  terms_text: '',
  declaration_text: '',
}

export const DEFAULT_DOCUMENT_DISPLAY_CONFIG: DocumentDisplayConfig = {
  b2b: { ...DEFAULT_DOMESTIC_CFG },
  export: { ...DEFAULT_EXPORT_CFG },
  b2c: { ...DEFAULT_DOMESTIC_CFG },
  proforma: { ...DEFAULT_DOMESTIC_CFG },
  credit_note: { ...DEFAULT_DOMESTIC_CFG },
  debit_note: { ...DEFAULT_DOMESTIC_CFG },
  vendor_bill: { ...DEFAULT_DOMESTIC_CFG },
  vendor_credit_note: { ...DEFAULT_DOMESTIC_CFG },
  purchase_order: { ...DEFAULT_DOMESTIC_CFG },
  customer_purchase_order: { ...DEFAULT_DOMESTIC_CFG },
  ...DEFAULT_SHARED_DOCUMENT_SETTINGS,
}

export interface AppSettings {
  // Organization
  organizationName: string
  baseCurrency: string
  fiscalYearStart: string // '01', '04', '07', '10'

  // Company Details
  companyBuilding?: string
  companyAddress?: string
  companyCity?: string
  companyState?: string
  companyPostalCode?: string
  companyCountry?: string
  companyPhone?: PhoneValue | null
  companyEmail?: string
  companyWebsite?: string
  companyGSTIN?: string // GST Identification Number (address-level identifier)
  companyPAN?: string // Permanent Account Number
  companyCIN?: string // Corporate Identification Number
  companyTAN?: string // Tax Deduction Account Number
  companyIEC?: string // Import Export Code
  companyLUTNumber?: string // Letter of Undertaking number
  companyFSSAI?: string // FSSAI License Number
  companyMSME?: string // MSME / Udyam Registration Number
  companyLogo?: string // Base64 encoded image

  // Document Display Configuration
  documentDisplayConfig: DocumentDisplayConfig

  // Display
  dateFormat: DateFormat
  numberFormat: NumberFormat

  // Defaults
  defaultDeliveryType?: DeliveryType
  businessType?: BusinessType
  defaultCreditDays?: number

  // Theme
  theme: 'light' | 'dark' | 'system'
}

export interface SettingsContextValue {
  settings: AppSettings
  updateSettings: (updates: Partial<AppSettings>) => void
  resetSettings: () => void
  isLoaded: boolean
}

// ============================================================================
// Defaults
// ============================================================================

const DEFAULT_SETTINGS: AppSettings = {
  organizationName: '',
  baseCurrency: 'INR',
  fiscalYearStart: '01',
  dateFormat: 'yyyy-MM-dd',
  numberFormat: 'en-US',
  defaultDeliveryType: 'intrastate',
  businessType: 'physical',
  theme: 'system',
  documentDisplayConfig: DEFAULT_DOCUMENT_DISPLAY_CONFIG,
}

// Settings persist PER ORGANIZATION. The pre-2026-07 key was a single
// global 'elaccounts_settings' blob shared by every org/workspace the
// browser visited — company identity typed in one org leaked into every
// other org's Company Settings screen (and localStorage-only values
// masked the fact that the server had nothing). Key template + one-time
// purge below fix that.
export function settingsStorageKey(orgId: string): string {
  return `elaccounts_settings_${orgId}`
}

// One-time cleanup of the legacy shared blob. Also removes every
// 'elaccounts_settings_synced_<orgId>' first-sync flag: those flags
// recorded "this org already seeded from the server" against the POISONED
// shared blob — leaving them would stop orgs from re-seeding their real
// identity (entity) data from the server into the new empty per-org store.
const MIGRATION_MARKER = 'elaccounts_settings_org_scoped_v1'
function purgeLegacySharedSettings(): void {
  try {
    if (localStorage.getItem(MIGRATION_MARKER) === 'true') { return }
    localStorage.removeItem('elaccounts_settings')
    const stale: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k?.startsWith('elaccounts_settings_synced_')) { stale.push(k) }
    }
    stale.forEach(k => { localStorage.removeItem(k) })
    localStorage.setItem(MIGRATION_MARKER, 'true')
  } catch {
    // silently ignore
  }
}

// ============================================================================
// Context
// ============================================================================

const SettingsContext = createContext<SettingsContextValue | null>(null)

// ============================================================================
// Helper Functions
// ============================================================================

function loadSettingsFromStorage(storageKey: string): Partial<AppSettings> {
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<AppSettings>
      return parsed
    }
  } catch {
    // silently ignore
  }
  return {}
}

function saveSettingsToStorage(storageKey: string, settings: AppSettings): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(settings))
  } catch {
    // silently ignore
  }
}

// ============================================================================
// Custom Hooks
// ============================================================================

function useSettingsLoad(
  organizationId: string,
  setSettings: (setter: (prev: AppSettings) => AppSettings) => void,
): string {
  // loadedOrg — which org's blob the in-memory settings currently hold.
  // '' until the first hydration. Re-runs on every org switch: state is
  // RESET to defaults first so org A's values never bleed into org B's
  // screen, then org B's own blob (if any) is layered on top.
  const [loadedOrg, setLoadedOrg] = useState('')
  useEffect(() => {
    if (!organizationId) { return }
    const loaded = loadSettingsFromStorage(settingsStorageKey(organizationId))
    setSettings(() => ({ ...DEFAULT_SETTINGS, ...loaded }))
    setLoadedOrg(organizationId)
  }, [organizationId, setSettings])
  return loadedOrg
}

function useSettingsPersistence(organizationId: string, settings: AppSettings, loadedOrg: string): void {
  useEffect(() => {
    // Write ONLY when the in-memory settings belong to the current org —
    // during an org switch there's a window where `settings` still holds
    // the previous org's values; persisting then would poison the new
    // org's blob (the exact cross-org leak this store used to have).
    if (organizationId && loadedOrg === organizationId) {
      saveSettingsToStorage(settingsStorageKey(organizationId), settings)
    }
  }, [organizationId, settings, loadedOrg])
}

function useSettingsActions(organizationId: string, setSettings: (setter: (prev: AppSettings) => AppSettings) => void): {
  updateSettings: (updates: Partial<AppSettings>) => void
  resetSettings: () => void
} {
  const updateSettings = useCallback((updates: Partial<AppSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }))
  }, [setSettings])

  const resetSettings = useCallback(() => {
    setSettings(() => DEFAULT_SETTINGS)
    if (organizationId) { localStorage.removeItem(settingsStorageKey(organizationId)) }
  }, [organizationId, setSettings])

  return { updateSettings, resetSettings }
}

// ============================================================================
// Provider
// ============================================================================

export function SettingsProvider({ children }: { children: ReactNode }): JSX.Element {
  const { organizationId } = useOrganization()
  // Purge the legacy shared blob synchronously, before children mount and
  // before anything reads the per-org sync flags. useState initializer =
  // runs exactly once per app load.
  useState(() => { purgeLegacySharedSettings(); return true })
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS)
  const loadedOrg = useSettingsLoad(organizationId, setSettings)
  useSettingsPersistence(organizationId, settings, loadedOrg)
  const isLoaded = loadedOrg !== '' && loadedOrg === organizationId
  const { updateSettings, resetSettings } = useSettingsActions(organizationId, setSettings)

  return (
    <SettingsContext.Provider
      value={{
        settings,
        updateSettings,
        resetSettings,
        isLoaded,
      }}
    >
      {children}
    </SettingsContext.Provider>
  )
}

// ============================================================================
// Hook
// ============================================================================

export function useSettings(): SettingsContextValue {
  const context = useContext(SettingsContext)
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Get the current date format setting
 */
export function useDateFormat(): DateFormat {
  const { settings } = useSettings()
  return settings.dateFormat
}

/**
 * Get the current number format setting
 */
export function useNumberFormat(): NumberFormat {
  const { settings } = useSettings()
  return settings.numberFormat
}
